import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DiscoveryFavoritesUserList from './DiscoveryFavoritesUserList';
import { DiscoveryService } from '../service';
import {
    DiscoveryFavoriteResponse,
    DiscoveryReactionEnum,
    FavoritesSortType,
    IDiscoveryListTotals,
    IFavoritesFilter,
    IProfileIds,
} from '../dto/IDiscoveryFilter';
import { IAllStates } from '../../../../../store/rootReducer';
import DiscoveryFavoritesListItem from './DiscoveryFavoritesListItem';
import { BackButton } from '../../../../../shared/components/button/BackButton';
import { ErrorToast, SuccessToast } from '../../../../../utils/toasters';
import { Pagination } from '../../../../../shared/components/Pagination';
import { scrollToTop } from '../../../../../utils/helpers/ScrollToTop';
import EmptyState from '../../../../../shared/components/placeholder/EmptyState';
import DiscoveryFavoritesNoteModal from './DiscoveryFavoritesNoteModal';
import DiscoveryFavoritesClientNoteModal from './DiscoveryFavoritesClientNoteModal';
import { Button, Col, Row, Spinner } from 'react-bootstrap';
import { downloadFileRequest } from '../../../../../utils';
import { BASE_URL } from '../../../../../utils/endpoints';
import { ExternalSearchDetailModal } from '../../book/external-search/components/modal';
import BookingOfferModal from '../../book/offer/modal';
import { setInitialState } from '../../../../../store/brand/offerBrandSlice';
import { BookingContractModal } from '../../contracts/contract/modal/BookingContractModal';
import { BookingService } from '../../book/service';
import { gtmService } from '../../../../../utils/gtm.service';
import { IDeclineOffer } from '../../../../influencer/offer/dto/IDeclineOffer';
import { ContractService } from '../../contracts/service';
import { ConfirmationModal } from '../../../../../shared/components/modal/ConfirmationModal';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DiscoveryDetailKPI from '../components/DiscoveryDetailsKPI';
import FavoriteUserListHeaderSearch from './FavoriteUserListHeaderSearch';
import { useCollaborations } from '../../../../../shared/hooks/useCollaborations';
import { DiscoveryFavoritesRowSkeleton } from './DiscoveryFavoritesRowSkeleton';

const DiscoveryFavorites: React.FC = () => {
    const { activeCampaignId, campaign } = useSelector((state: IAllStates) => state.selectedCampaign);
    const [favoriteUsers, setFavoriteUsers] = useState<DiscoveryFavoriteResponse[]>();
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [showClientNoteModal, setShowClientNoteModal] = useState(false);
    const [selectedFavorite, setSelectedFavorite] = useState<DiscoveryFavoriteResponse>();
    const [updateList, setUpdateList] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const triggerRefresh = useCallback(() => setRefreshKey(prev => prev + 1), []);
    const [totalPages, setTotalPages] = useState(0);
    const [isClient, setIsClient] = useState(false);
    const [listTotals, setListTotals] = useState<IDiscoveryListTotals>();
    const [totalCount, setTotalCount] = useState(0);
    const [filterParams, setFilterParams] = useState<IFavoritesFilter>({
        page: 1,
        perPage: 10,
        sortType: 'DESC',
        sortBy: FavoritesSortType.PRIORITY,
    });
    const [filterCollaborations, setFilterCollaborations] = useState<{ username: string }>({ username: '' });
    const { collaborations } = useCollaborations(filterCollaborations);
    const [isInternalUser, setIsInternalUser] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showExternalProfile, setShowExternalProfile] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showContractInviteModal, setShowContractInviteModal] = useState(false);
    const [showNegotiateModal, setShowNegotiateModal] = useState(false);
    const [showContractNegotiateModal, setShowContractNegotiateModal] = useState(false);
    const [declineReasons, setDeclineReasons] = useState<IDeclineOffer>({ declineReason: '', declineComment: '' });
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [profileIds, setProfileIds] = useState<IProfileIds>({
        ivId: undefined,
        externalId: undefined,
        ivProfileId: undefined,
        offerId: undefined,
    });
    const [removeUserIds, setRemoveUserIds] = useState<IProfileIds>({
        ivId: undefined,
        externalId: undefined,
    });
    const { discoveryDetails } = useSelector((state: IAllStates) => state.auth.user);
    const dispatch = useDispatch();
    const location = useLocation();
    const isBundleHeader = location.pathname.split('/').includes('options');
    const { t } = useTranslation();
    const [isListLoading, setIsListLoading] = useState(true);
    const lastQueryKeyRef = useRef<string>();
    useEffect(() => {
        const queryKey = `${activeCampaignId}-${JSON.stringify(filterParams)}`;
        if (lastQueryKeyRef.current !== queryKey) {
            lastQueryKeyRef.current = queryKey;
            setIsListLoading(true);
        }

        let isCurrent = true;
        const fetchFavorites = async () => {
            try {
                const response = await DiscoveryService.getFavoriteUsers(filterParams, activeCampaignId);
                if (!isCurrent) return;
                setFavoriteUsers(response.data);
                setTotalPages(Math.ceil(response.count / response.perPage));
                setTotalCount(response.count);
                setIsClient(response?.info?.isClient);
                setListTotals(response?.info?.listTotals);
            } catch (error) {
                if (isCurrent) ErrorToast(error);
            } finally {
                if (isCurrent) setIsListLoading(false);
            }
        };
        fetchFavorites();

        return () => {
            isCurrent = false;
        };
    }, [filterParams, updateList, refreshKey, activeCampaignId]);

    const userReactionAction = useCallback(
        async (listId: number, reaction: DiscoveryReactionEnum) => {
            try {
                DiscoveryService.addReactions({ listId, reaction }).then(() => {
                    setUpdateList(prev => !prev);
                });
            } catch (error) {
                ErrorToast(error);
            }
        },
        [activeCampaignId]
    );

    const removeFromFavorites = useCallback(async () => {
        try {
            await DiscoveryService.addFavoriteUser({
                campaignId: activeCampaignId ?? 0,
                ivId: removeUserIds?.ivId ?? 0,
                externalId: removeUserIds?.externalId ?? 0,
            });
            SuccessToast(t('influencer.explore.removeFromFavoriteSuccess'));

            // const newTotalCount = totalCount - 1;

            if (favoriteUsers && favoriteUsers.length === 1 && filterParams.page > 1) {
                setFilterParams(prev => ({
                    ...prev,
                    page: prev.page - 1,
                }));
            } else {
                setUpdateList(prev => !prev);
            }
        } catch (error) {
            ErrorToast(error);
        }
    }, [favoriteUsers, filterParams, totalCount, removeUserIds]);

    const handlePages = useCallback((updatePage: number) => {
        setFilterParams(prev => ({ ...prev, page: updatePage }));
        scrollToTop();
    }, []);

    const openNoteModalAction = useCallback(
        (favorite: DiscoveryFavoriteResponse) => {
            // Prevent opening the modal for client users if there is no note
            if (isClient && !favorite.note) {
                return;
            }
            setSelectedFavorite(favorite);
            setShowNoteModal(true);
        },
        [isClient, setSelectedFavorite, setShowNoteModal]
    );

    const closeModalAction = useCallback(() => {
        setShowNoteModal(false);
        setSelectedFavorite(undefined);
    }, []);

    const openClientNoteModalAction = useCallback(
        (favorite: DiscoveryFavoriteResponse) => {
            // Prevent opening the modal for supplier users if there is no client note
            if (!isClient && !favorite.clientNote) {
                return;
            }
            setSelectedFavorite(favorite);
            setShowClientNoteModal(true);
        },
        [isClient, setSelectedFavorite, setShowClientNoteModal]
    );

    const closeClientNoteModalAction = useCallback(() => {
        setShowClientNoteModal(false);
        setSelectedFavorite(undefined);
    }, []);

    const openModal = (
        externalId?: number,
        ivId?: number,
        ivProfileId?: number,
        internalUser?: boolean,
        hasOffer?: boolean,
        offerId?: number
    ) => {
        if (isClient) {
            setShowExternalProfile(true);
        } else {
            if (internalUser && campaign?.isLongTerm) {
                hasOffer ? setShowContractNegotiateModal(true) : setShowContractInviteModal(true);
            } else if (internalUser && !campaign?.isLongTerm) {
                hasOffer ? setShowNegotiateModal(true) : setShowInviteModal(true);
            } else {
                setShowExternalProfile(true);
            }
        }
        setIsInternalUser(internalUser ?? false);
        setProfileIds(prevState => ({
            ...prevState,
            ivId,
            externalId,
            ivProfileId,
            offerId,
        }));
    };

    const closeExternalModal = () => {
        setShowExternalProfile(false);
        setUpdateList(prev => !prev);
    };

    const handleCloseInviteModal = () => {
        setShowInviteModal(false);
        setShowContractInviteModal(false);
        triggerRefresh();
        dispatch(setInitialState());
    };

    const handleCloseNegotiateModal = () => {
        setShowNegotiateModal(false);
        setShowContractNegotiateModal(false);
        triggerRefresh();
        dispatch(setInitialState());
    };

    function acceptOffer(offerId: number) {
        BookingService.acceptOffer(offerId)
            .then(() => {
                gtmService('brand_offer_accepted');
                SuccessToast(t('brand.campaign.book.offer.accept_offer'));
            })
            .catch(error => ErrorToast(error));
    }

    function declineOffer(offerId: number) {
        BookingService.declineOffer(offerId, declineReasons)
            .then(() => {
                SuccessToast(t('brand.campaign.book.offer.decline_offer'));
            })
            .catch(error => ErrorToast(error));
    }

    function acceptContract(offerId: number) {
        ContractService.acceptContract(offerId)
            .then(() => {
                gtmService('brand_offer_accepted');
                SuccessToast(t('brand.campaign.book.offer.accept_offer'));
            })
            .catch(error => ErrorToast(error));
    }

    function declineContract(offerId: number) {
        ContractService.declineContract(offerId, declineReasons)
            .then(() => {
                SuccessToast(t('brand.campaign.book.offer.decline_offer'));
            })
            .catch(error => ErrorToast(error));
    }

    const showConfirmationModalAndSetIds = (ivId: number, externalId: number) => {
        setShowConfirmModal(true);
        setRemoveUserIds(prevState => ({ ...prevState, externalId, ivId }));
    };

    return (
        <>
            {showConfirmModal && (
                <ConfirmationModal
                    title={t('brand.campaign.discovery.remove_from_favorites')}
                    description={t('brand.campaign.book.offer.modal.are_you_sure')}
                    show={showConfirmModal}
                    closeModal={() => setShowConfirmModal(false)}
                    action={removeFromFavorites}
                />
            )}
            {showNoteModal && selectedFavorite && (
                <DiscoveryFavoritesNoteModal
                    show={showNoteModal}
                    user={selectedFavorite}
                    setUpdateFavoritesList={setUpdateList}
                    closeModal={closeModalAction}
                    isClient={isClient}
                />
            )}
            {showClientNoteModal && selectedFavorite && (
                <DiscoveryFavoritesClientNoteModal
                    show={showClientNoteModal}
                    user={selectedFavorite}
                    setUpdateFavoritesList={setUpdateList}
                    closeModal={closeClientNoteModalAction}
                    isClient={isClient}
                />
            )}
            {showExternalProfile && (
                <ExternalSearchDetailModal
                    id={profileIds?.ivId ?? profileIds?.externalId ?? 0}
                    isInternal={isInternalUser}
                    show={showExternalProfile}
                    closeModal={closeExternalModal}
                />
            )}
            {showInviteModal && (
                <BookingOfferModal
                    show={showInviteModal}
                    closeModal={handleCloseInviteModal}
                    modalStatus="invite"
                    userId={profileIds.ivProfileId}
                    passedCampaignId={activeCampaignId}
                />
            )}

            {showContractInviteModal && (
                <BookingContractModal
                    show={showContractInviteModal}
                    closeModal={handleCloseInviteModal}
                    modalStatus="invite"
                    userId={profileIds.ivProfileId}
                    passedCampaignId={activeCampaignId}
                />
            )}

            {showNegotiateModal && (
                <BookingOfferModal
                    show={showNegotiateModal}
                    closeModal={handleCloseNegotiateModal}
                    userId={profileIds.ivProfileId}
                    passedCampaignId={activeCampaignId}
                    offerId={profileIds.offerId}
                    setUpdateList={triggerRefresh}
                    acceptOffer={acceptOffer}
                    declineOffer={declineOffer}
                    setDeclineReason={setDeclineReasons}
                />
            )}

            {showContractNegotiateModal && (
                <BookingContractModal
                    show={showContractNegotiateModal}
                    closeModal={handleCloseNegotiateModal}
                    userId={profileIds.ivProfileId}
                    passedCampaignId={activeCampaignId}
                    contractId={profileIds.offerId}
                    setUpdateList={triggerRefresh}
                    acceptContract={acceptContract}
                    declineContract={declineContract}
                    setDeclineReason={setDeclineReasons}
                />
            )}

            <div className="mb-2">
                {isBundleHeader ? (
                    <div>
                        <h4 className="mb-0">{t('brand.campaign.discovery.influencer_list')}</h4>
                        <p className="mb-0">
                            <strong className="text-primary mr-1">
                                {discoveryDetails?.discoveryProfileViewsLeft ?? 0}
                            </strong>
                            {t('brand.campaign.discovery.left_views')}
                        </p>
                    </div>
                ) : (
                    <div className="d-flex align-items-center">
                        <BackButton page={'discovery_search'} />
                        <h5 className="mb-0 ms-2">{t('brand.campaign.discovery.back')}</h5>
                    </div>
                )}
            </div>
            <DiscoveryDetailKPI listTotals={listTotals} isLoading={isListLoading && !listTotals} />
            <Row className="align-items-center my-2">
                <FavoriteUserListHeaderSearch
                    setFilterParams={setFilterParams}
                    collaborations={collaborations}
                    setFilterCollaborations={setFilterCollaborations}
                    isFilterLoading={isListLoading}
                />
                <Col className="text-end">
                    <Button
                        variant="primary"
                        onClick={() =>
                            downloadFileRequest(
                                BASE_URL + 'brand/discovery/export/modash/favorites',
                                null,
                                { campaignId: activeCampaignId },
                                setIsLoading
                            )
                        }
                        disabled={isLoading || isListLoading || !favoriteUsers?.length}
                    >
                        {isLoading ? (
                            <>
                                Generating export...{' '}
                                <Spinner animation="border" size="sm" role="status" className="ms-1" />
                            </>
                        ) : (
                            t('brand.campaign.discovery.export_page')
                        )}
                    </Button>
                </Col>
            </Row>

            <DiscoveryFavoritesUserList
                filterParams={filterParams}
                setFilterParams={setFilterParams}
                totalCount={totalCount}
                isClient={isClient}
                isGlobal={false}
                isBundle={isBundleHeader}
            >
                <>
                    {isListLoading
                        ? Array.from({ length: filterParams.perPage }).map((_, i) => (
                              <DiscoveryFavoritesRowSkeleton
                                  key={i}
                                  isClient={isClient}
                                  isGlobal={false}
                                  isBundle={isBundleHeader}
                              />
                          ))
                        : favoriteUsers?.map(profile => (
                              <DiscoveryFavoritesListItem
                                  key={profile.id}
                                  id={profile.id}
                                  removeFromFavorites={showConfirmationModalAndSetIds}
                                  addReaction={userReactionAction}
                                  favorite={profile}
                                  openNoteModal={openNoteModalAction}
                                  openClientNoteModal={openClientNoteModalAction}
                                  openExternalModal={openModal}
                                  isClient={isClient}
                                  setRefreshList={setUpdateList}
                                  isGlobal={false}
                                  isBundle={isBundleHeader}
                              />
                          ))}
                </>
            </DiscoveryFavoritesUserList>

            {!isListLoading && favoriteUsers?.length === 0 && <EmptyState />}

            {totalPages > 1 && (
                <Pagination page={filterParams.page} totalPages={totalPages} handlePagination={handlePages} />
            )}
        </>
    );
};

export default DiscoveryFavorites;
